import { curvanceConfigFor } from '@1delta/data-sdk'
import {
  CurvanceCentralRegistryAbi,
  CurvanceCTokenAbi,
  CurvanceMarketManagerAbi,
  CurvanceProtocolReaderAbi,
} from '@1delta/abis'
import {
  getEvmClientUniversal,
  multicallRetryUniversal,
} from '@1delta/providers'
import { setCurvanceAssetRoster } from '../../../prices/oracle-prices/fetchers/curvanceRoster'
import type {
  CurvanceMarketRaw,
  CurvanceMarketsRaw,
  CurvanceTokenRaw,
} from './types'

/**
 * Fetch every Curvance market on a chain — FULLY ON-CHAIN, no API.
 *
 * There are two paths, and the fast one is the default:
 *
 *  1. **`ProtocolReader`** (configured): `getStaticMarketData()` +
 *     `getAllDynamicState(0x0)` return the entire book — every market, both
 *     legs, risk config, caps, pause flags, live rates, utilization AND oracle
 *     prices — in TWO eth_calls. Nothing else we integrate comes close.
 *  2. **Plain multicall fallback** (no reader, or the reader reverts): discover
 *     via `CentralRegistry.marketManagers()` → `queryTokensListed()`, then batch
 *     the individual getters. Slower and it cannot produce prices, but it keeps
 *     the provider alive if the reader is ever redeployed out from under the
 *     config.
 *
 * Discovery is on-chain in BOTH paths and deliberately so: Curvance governance
 * lists markets permissionlessly and freezes them with caps/pause flags rather
 * than delisting, so a static roster would both miss new markets and advertise
 * frozen ones as live.
 *
 * The `0x0` account passed to `getAllDynamicState` is not a placeholder we
 * ignore — the reader returns market state and user state together, and the
 * zero account simply yields an empty user half. User positions come from the
 * separate user-data path.
 */

/** Calls per token in the fallback path — keep in lock-step with the slicer. */
const FALLBACK_TOKEN_READS = 12

const zero = '0x0000000000000000000000000000000000000000'

const big = (v: any): bigint => {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  if (typeof v === 'string' && v !== '0x') {
    try {
      return BigInt(v)
    } catch {}
  }
  return 0n
}

const num = (v: any, fallback: number): number => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const str = (v: any, fallback: string): string =>
  typeof v === 'string' && v.length > 0 ? v : fallback

export async function fetchCurvanceMarkets(
  lender: string,
  chainId: string,
): Promise<CurvanceMarketsRaw> {
  const config = curvanceConfigFor(lender, chainId)
  if (!config?.centralRegistry) return { lender, config, markets: [] }

  let result: CurvanceMarketsRaw = { lender, config, markets: [] }
  if (config.protocolReader) {
    result = await fetchViaReader(lender, chainId, config)
  }
  if (result.markets.length === 0) {
    result = await fetchViaMulticall(lender, chainId, config)
  }

  // Publish the discovered market -> assets map for the oracle-price fetcher,
  // whose `getCalls` is synchronous and so cannot discover markets itself. See
  // `curvanceRoster.ts` for why re-discovering there would be wrong rather than
  // just wasteful.
  setCurvanceAssetRoster(
    chainId,
    lender,
    result.markets.map((m) => ({
      marketManager: m.marketManager,
      assets: m.tokens
        .map((t) => t.asset.toLowerCase())
        .filter((a) => a && a !== zero),
    })),
  )

  return result
}

/**
 * The two-call path.
 *
 * These are issued as DIRECT `eth_call`s, deliberately NOT through
 * `multicallRetryUniversal`. Both reader functions loop over every market and
 * every leg, and wrapping them in a Multicall3 `aggregate3` makes the inner
 * call run out of gas — which `allowFailure: true` then maps to `'0x'`, i.e. a
 * SILENT empty read. Measured on Monad 2026-08-07: the direct call returns all
 * 25 markets, the identical call inside `aggregate3` returns `'0x'`. Batching
 * two calls buys nothing anyway.
 */
/**
 * `MIN_LOAN_SIZE` per market manager, WAD USD.
 *
 * A separate round because the ProtocolReader does not return it, and it is a
 * hard gate at BOTH ends of a position (open AND partial repay) — so the term
 * sheet cannot describe the market honestly without it.
 *
 * This one IS safe to batch, unlike the reader's two calls: it is a plain
 * constant getter, not a loop over every market, so it cannot OOG inside
 * `aggregate3` the way `getStaticMarketData` does.
 *
 * On failure the entry is simply absent and callers fall back to `0n`, which
 * the term-sheet layer reads as "no floor known" rather than "no floor" — a
 * missing read must never present as a permissive one.
 */
async function fetchMinLoanSizes(
  chainId: string,
  marketManagers: string[],
): Promise<Map<string, bigint>> {
  const out = new Map<string, bigint>()
  if (marketManagers.length === 0) return out
  try {
    const res = (await multicallRetryUniversal({
      chain: chainId,
      calls: marketManagers.map((mm) => ({
        address: mm,
        name: 'MIN_LOAN_SIZE',
        params: [],
      })),
      abi: marketManagers.map(() => CurvanceMarketManagerAbi) as any,
      allowFailure: true,
    })) as any[]
    marketManagers.forEach((mm, i) => {
      const v = res?.[i]
      if (typeof v === 'bigint') out.set(mm.toLowerCase(), v)
      else if (typeof v === 'string' && /^\d+$/.test(v))
        out.set(mm.toLowerCase(), BigInt(v))
    })
  } catch {
    // Leave the map empty — see the note above.
  }
  return out
}

async function fetchViaReader(
  lender: string,
  chainId: string,
  config: NonNullable<ReturnType<typeof curvanceConfigFor>>,
): Promise<CurvanceMarketsRaw> {
  const reader = config.protocolReader! as `0x${string}`
  let staticData: any[] = []
  let dynamicData: any[] = []

  // A handful of RPC ids, since there is no multicall layer doing failover for
  // us on this path.
  for (let rpcId = 0; rpcId < 3 && staticData.length === 0; rpcId++) {
    try {
      const client = getEvmClientUniversal({ chain: chainId, rpcId })
      const [staticRes, dynamicRes] = await Promise.all([
        client.readContract({
          address: reader,
          abi: CurvanceProtocolReaderAbi as any,
          functionName: 'getStaticMarketData',
        }),
        client
          .readContract({
            address: reader,
            abi: CurvanceProtocolReaderAbi as any,
            functionName: 'getAllDynamicState',
            args: [zero],
          })
          // The dynamic half is the expensive one. If only IT fails we still
          // publish the markets, just without live rates or prices — better
          // than dropping the whole book.
          .catch(() => undefined),
      ])
      if (Array.isArray(staticRes)) staticData = staticRes as any[]
      // `getAllDynamicState` returns a 2-tuple `(market[], user)`; we want the
      // first half.
      if (Array.isArray(dynamicRes) && Array.isArray(dynamicRes[0])) {
        dynamicData = dynamicRes[0] as any[]
      }
    } catch {
      // Try the next RPC.
    }
  }
  if (staticData.length === 0) return { lender, config, markets: [] }

  // Index dynamic state by market then token — the two calls return markets in
  // the same order today, but pairing by ADDRESS rather than by index means a
  // future reader that filters one list and not the other cannot silently
  // attach one market's rates to another's config.
  const dynamicByToken = new Map<string, any>()
  for (const m of dynamicData) {
    for (const t of m?.tokens ?? []) {
      if (t?._address) dynamicByToken.set(String(t._address).toLowerCase(), t)
    }
  }

  const allow = allowlist(config)
  const markets: CurvanceMarketRaw[] = []
  for (const m of staticData) {
    const marketManager = String(m?._address ?? '')
    if (!marketManager.startsWith('0x')) continue
    if (allow && !allow.has(marketManager.toLowerCase())) continue

    const tokens: CurvanceTokenRaw[] = []
    for (const t of m?.tokens ?? []) {
      const cToken = String(t?._address ?? '')
      if (!cToken.startsWith('0x')) continue
      // A token the reader reports as unlisted is not part of the market.
      if (t?.isListed === false) continue
      const d = dynamicByToken.get(cToken.toLowerCase())
      tokens.push({
        cToken,
        symbol: str(t?.symbol, 'c?'),
        name: str(t?.name, 'Curvance'),
        decimals: num(t?.decimals, 18),
        asset: String(t?.asset?._address ?? zero),
        assetSymbol: str(t?.asset?.symbol, '?'),
        assetName: str(t?.asset?.name, '?'),
        assetDecimals: num(t?.asset?.decimals, 18),

        collRatio: big(t?.collRatio),
        collReqSoft: big(t?.collReqSoft),
        collReqHard: big(t?.collReqHard),
        liqIncBase: big(t?.liqIncBase),
        liqIncCurve: big(t?.liqIncCurve),
        closeFactorBase: big(t?.closeFactorBase),
        maxLeverage: big(t?.maxLeverage),

        collateralCap: big(t?.collateralCap),
        debtCap: big(t?.debtCap),
        mintPaused: !!t?.mintPaused,
        collateralizationPaused: !!t?.collateralizationPaused,
        borrowPaused: !!t?.borrowPaused,
        redeemPaused: !!t?.redeemPaused,

        totalAssets: big(d?.totalAssets),
        totalSupply: big(d?.totalSupply),
        exchangeRate: big(d?.exchangeRate),
        collateral: big(d?.collateral),
        debt: big(d?.debt),
        liquidity: big(d?.liquidity),
        assetPrice: big(d?.assetPrice),
        assetPriceLower: big(d?.assetPriceLower),
        borrowRate: big(d?.borrowRate),
        predictedBorrowRate: big(d?.predictedBorrowRate),
        supplyRate: big(d?.supplyRate),
        utilizationRate: big(d?.utilizationRate),

        interestFee: big(t?.interestFee),
        irmTargetRate: big(t?.irmTargetRate),
        irmMaxRate: big(t?.irmMaxRate),
        irmTargetUtilization: big(t?.irmTargetUtilization),
      })
    }
    if (tokens.length === 0) continue
    markets.push({
      marketManager,
      cooldownLength: big(m?.cooldownLength),
      // Filled in below — one batched round for every market at once.
      minLoanSizeUsdWad: 0n,
      tokens,
    })
  }

  const minLoans = await fetchMinLoanSizes(
    chainId,
    markets.map((mk) => mk.marketManager),
  )
  for (const mk of markets) {
    mk.minLoanSizeUsdWad =
      minLoans.get(mk.marketManager.toLowerCase()) ?? 0n
  }

  return { lender, config, markets }
}

/**
 * The fallback path. Produces everything except oracle prices (which only the
 * reader and the OracleManager can supply) — the price fetcher covers those
 * independently, so a market published from here is complete apart from the
 * `assetPrice` fields, which the converter treats as "unknown" rather than 0.
 */
async function fetchViaMulticall(
  lender: string,
  chainId: string,
  config: NonNullable<ReturnType<typeof curvanceConfigFor>>,
): Promise<CurvanceMarketsRaw> {
  // --- Round 1: market managers ---
  let managers: string[] = []
  try {
    const [res] = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        {
          address: config.centralRegistry,
          name: 'marketManagers',
          params: [],
        },
      ],
      abi: CurvanceCentralRegistryAbi as any,
      allowFailure: true,
    })) as any[]
    if (Array.isArray(res)) {
      managers = res.filter(
        (a: any) => typeof a === 'string' && a.startsWith('0x'),
      )
    }
  } catch {
    return { lender, config, markets: [] }
  }

  const allow = allowlist(config)
  if (allow) managers = managers.filter((m) => allow.has(m.toLowerCase()))
  if (managers.length === 0) return { lender, config, markets: [] }

  // --- Round 2: listed tokens + market-wide pause ---
  let listed: any[] = []
  try {
    listed = (await multicallRetryUniversal({
      chain: chainId,
      calls: managers.flatMap((mm) => [
        { address: mm, name: 'queryTokensListed', params: [] },
        { address: mm, name: 'redeemPaused', params: [] },
      ]),
      abi: managers.flatMap(() => [
        CurvanceMarketManagerAbi,
        CurvanceMarketManagerAbi,
      ]) as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return { lender, config, markets: [] }
  }

  const pairs: { mm: string; cToken: string; redeemPaused: boolean }[] = []
  managers.forEach((mm, i) => {
    const toks = listed[i * 2]
    // `2` is paused; `1` is live. Anything else means the read failed, and
    // "failed" must not read as "paused".
    const redeemPaused = big(listed[i * 2 + 1]) === 2n
    if (!Array.isArray(toks)) return
    for (const t of toks) {
      if (typeof t === 'string' && t.startsWith('0x')) {
        pairs.push({ mm, cToken: t, redeemPaused })
      }
    }
  })
  if (pairs.length === 0) return { lender, config, markets: [] }

  // --- Round 3: per-token config + state ---
  const calls: any[] = []
  const abis: any[] = []
  for (const { mm, cToken } of pairs) {
    calls.push(
      { address: mm, name: 'collConfig', params: [cToken] },
      { address: mm, name: 'liquidationConfig', params: [cToken] },
      { address: mm, name: 'collateralCaps', params: [cToken] },
      { address: mm, name: 'debtCaps', params: [cToken] },
      { address: mm, name: 'actionsPaused', params: [cToken] },
      { address: cToken, name: 'symbol', params: [] },
      { address: cToken, name: 'decimals', params: [] },
      { address: cToken, name: 'asset', params: [] },
      { address: cToken, name: 'totalAssets', params: [] },
      { address: cToken, name: 'marketOutstandingDebt', params: [] },
      { address: cToken, name: 'assetsHeld', params: [] },
      { address: cToken, name: 'interestFee', params: [] },
    )
    abis.push(
      CurvanceMarketManagerAbi,
      CurvanceMarketManagerAbi,
      CurvanceMarketManagerAbi,
      CurvanceMarketManagerAbi,
      CurvanceMarketManagerAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
      CurvanceCTokenAbi,
    )
  }

  // Chunked per token-group rather than issued as one ~600-call request. Under
  // `allowFailure: true` the multicall layer only retries when EVERY call in a
  // request fails, so a partial transport failure (which public RPCs do return
  // mid-batch) is mapped to `'0x'` and would surface as a live market with a
  // null LTV. Chunking makes such a failure all-fail WITHIN its chunk, which
  // the layer then retries and fails over. Same lesson as Resupply.
  const TOKENS_PER_BATCH = 8
  const results: any[] = []
  for (let i = 0; i < pairs.length; i += TOKENS_PER_BATCH) {
    const from = i * FALLBACK_TOKEN_READS
    const to =
      Math.min(i + TOKENS_PER_BATCH, pairs.length) * FALLBACK_TOKEN_READS
    try {
      const chunk = (await multicallRetryUniversal({
        chain: chainId,
        calls: calls.slice(from, to),
        abi: abis.slice(from, to) as any,
        allowFailure: true,
      })) as any[]
      results.push(...chunk)
    } catch {
      results.push(...new Array(to - from).fill('0x'))
    }
  }

  // Resolve the underlying assets' metadata in one extra hop — we only learn
  // their addresses from round 3.
  const assets = pairs.map((_, i) => results[i * FALLBACK_TOKEN_READS + 7])
  let assetMeta: any[] = []
  try {
    assetMeta = (await multicallRetryUniversal({
      chain: chainId,
      calls: assets.flatMap((a: any) => [
        { address: a ?? zero, name: 'symbol', params: [] },
        { address: a ?? zero, name: 'decimals', params: [] },
        { address: a ?? zero, name: 'name', params: [] },
      ]),
      abi: assets.flatMap(() => [
        CurvanceCTokenAbi,
        CurvanceCTokenAbi,
        CurvanceCTokenAbi,
      ]) as any,
      allowFailure: true,
    })) as any[]
  } catch {
    assetMeta = []
  }

  const byManager = new Map<string, CurvanceMarketRaw>()
  pairs.forEach((p, i) => {
    const base = i * FALLBACK_TOKEN_READS
    const coll = results[base]
    const liq = results[base + 1]
    const paused = results[base + 4]
    const asset = results[base + 7]
    // A failed config read is NOT a zeroed market — publishing it would render
    // a live market at 0% LTV and frozen, which is indistinguishable from a
    // genuinely retired one. Skip; the next refresh republishes it.
    if (!Array.isArray(coll) || typeof asset !== 'string') return

    const tuple = (v: any, idx: number) => (Array.isArray(v) ? big(v[idx]) : 0n)
    const held = big(results[base + 10])
    const debt = big(results[base + 9])

    const token: CurvanceTokenRaw = {
      cToken: p.cToken,
      symbol: str(results[base + 5], 'c?'),
      name: str(results[base + 5], 'Curvance'),
      decimals: num(results[base + 6], 18),
      asset,
      assetSymbol: str(assetMeta[i * 3], '?'),
      assetDecimals: num(assetMeta[i * 3 + 1], 18),
      assetName: str(assetMeta[i * 3 + 2], '?'),

      collRatio: tuple(coll, 0),
      collReqSoft: tuple(coll, 1),
      collReqHard: tuple(coll, 2),
      liqIncBase: tuple(liq, 0),
      liqIncCurve: tuple(liq, 1),
      closeFactorBase: tuple(liq, 4),
      maxLeverage: 0n,

      collateralCap: big(results[base + 2]),
      debtCap: big(results[base + 3]),
      mintPaused: Array.isArray(paused) ? !!paused[0] : false,
      collateralizationPaused: Array.isArray(paused) ? !!paused[1] : false,
      borrowPaused: Array.isArray(paused) ? !!paused[2] : false,
      redeemPaused: p.redeemPaused,

      totalAssets: big(results[base + 8]),
      totalSupply: 0n,
      exchangeRate: 0n,
      collateral: 0n,
      debt,
      liquidity: held,
      // No reader ⇒ no prices from here. Left at 0 and treated as UNKNOWN by
      // the converter, which then falls back to the shared price map.
      assetPrice: 0n,
      assetPriceLower: 0n,
      borrowRate: 0n,
      predictedBorrowRate: 0n,
      supplyRate: 0n,
      utilizationRate:
        held + debt > 0n ? (debt * 10n ** 18n) / (held + debt) : 0n,

      interestFee: big(results[base + 11]),
      irmTargetRate: 0n,
      irmMaxRate: 0n,
      irmTargetUtilization: 0n,
    }

    const existing = byManager.get(p.mm)
    if (existing) existing.tokens.push(token)
    else
      byManager.set(p.mm, {
        marketManager: p.mm,
        // `MIN_HOLD_PERIOD` is a constant on every deployment; hardcoding the
        // known value here beats an extra round-trip in the fallback path.
        cooldownLength: 1200n,
        minLoanSizeUsdWad: 0n,
        tokens: [token],
      })
  })

  const out = [...byManager.values()]
  // `MIN_LOAN_SIZE` is NOT hardcoded the way the hold period above is: it is a
  // money value a DAO can move, and reporting a stale floor would under-state a
  // gate that reverts. One extra batched round is the right trade here.
  const minLoans = await fetchMinLoanSizes(
    chainId,
    out.map((mk) => mk.marketManager),
  )
  for (const mk of out) {
    mk.minLoanSizeUsdWad = minLoans.get(mk.marketManager.toLowerCase()) ?? 0n
  }

  return { lender, config, markets: out }
}

function allowlist(
  config: NonNullable<ReturnType<typeof curvanceConfigFor>>,
): Set<string> | undefined {
  if (!config.marketAllowlist || config.marketAllowlist.length === 0) {
    return undefined
  }
  return new Set(config.marketAllowlist.map((m) => m.toLowerCase()))
}
